import { useState, useMemo } from 'react';
import { Card, Group, Select, Tabs, Alert, Button, Stack, Text, List, ThemeIcon } from '@mantine/core';
import {
  IconChecklist, IconUsersGroup, IconCertificate, IconReportAnalytics,
  IconAlertTriangle, IconCalendarTime, IconSchool, IconBook, IconArrowRight, IconCircleCheck,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { useClasses, useSubjects, useAcademicYears } from '../hooks/useLookups';
import { useAcademicTerms, useCurrentTerm } from '../hooks/useAcademicTerms';
import { JournalMatrix } from '../components/journal/JournalMatrix';
import { BulkGrades } from '../components/journal/BulkGrades';
import { TermSummary } from '../components/journal/TermSummary';
import { GradeReport } from '../components/journal/GradeReport';
import { useAuth } from '../contexts/AuthContext';
import { canAccess } from '../lib/permissions';
import { DateInput } from '@mantine/dates';
import { uzDateProps } from '../lib/dateInput';

/**
 * Elektron jurnal (kontrakt 3-bo'lim).
 *
 * Sinf + fan + chorak tanlovi TEPADA, yagona joyda: to'rtala tab ham aynan shu
 * kesimda ishlaydi, tab almashganda tanlov yo'qolmaydi.
 *
 * BO'SH TIZIM: prod bazasi tozalangan — mijoz panelni birinchi marta ochganda
 * hech narsa yo'q. Shu sabab yetishmayotgan BIRINCHI qadam ko'rsatiladi (hammasi
 * bir vaqtda emas) va tabiiy tartib saqlanadi:
 *   o'quv yili → chorak → sinf → fan → o'quvchi → baho.
 */
/** Sana → "YYYY-MM-DD" (brauzer mahalliy vaqti = Toshkent). */
const kunKalit = (d: Date | null) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;

export default function Journal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageTerms = canAccess(user?.role, 'academic-years');

  const { data: years, isLoading: yearsLoading } = useAcademicYears();
  const { data: classes, isLoading: classesLoading } = useClasses();
  const { data: subjects, isLoading: subjectsLoading } = useSubjects();
  const { data: terms, isLoading: termsLoading } = useAcademicTerms();
  const { data: currentTerm } = useCurrentTerm();

  const [classId, setClassId] = useState<string | null>(null);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [termId, setTermId] = useState<string | null>(null);
  /* SANA FILTRI — faqat KO'RSATISHGA ta'sir qiladi. O'rtacha va taklif
     baribir butun chorak bo'yicha qoladi (serverda shunday qilingan):
     o'qituvchi bir haftani tanlaganda taklif ham o'zgarib ketsa, chorak
     bahosi tasodifiy chiqardi. */
  /* STANDART — OXIRGI 7 KUN. Butun chorakni ko'rsatish jadvalni o'nlab
     ustunga cho'zib yuborardi: o'qituvchi gorizontal aylantirib, bugungi
     ustunni qidirishga majbur bo'lardi. Kerak bo'lsa davr kengaytiriladi. */
  const [davr, setDavr] = useState<string>('7');
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  /* Tanlangan davrdan sana oralig'i. `chorak` — filtrsiz (butun chorak),
     `custom` — foydalanuvchi sanalarni qo'lda tanlagan. */
  const oraliq = useMemo(() => {
    if (davr === 'chorak') return { from: undefined, to: undefined };
    if (davr === 'custom') return { from: kunKalit(from), to: kunKalit(to) };
    const kun = Number(davr);
    const oxir = new Date();
    const bosh = new Date(oxir.getTime() - (kun - 1) * 86400000);
    return { from: kunKalit(bosh), to: kunKalit(oxir) };
  }, [davr, from, to]);
  const [tab, setTab] = useState<string | null>('matrix');

  // Chorak tanlanmagan bo'lsa — bugungi chorak, u ham bo'lmasa ro'yxatning birinchisi.
  const effectiveTermId = termId ?? currentTerm?.id ?? terms?.[0]?.id ?? null;

  const lookupsReady = !yearsLoading && !classesLoading && !subjectsLoading && !termsLoading;

  /**
   * Sozlash qadamlari — TABIIY TARTIBDA. Faqat BIRINCHI bajarilmagani ko'rsatiladi:
   * bo'sh tizimda to'rtta ogohlantirishni birdan chiqarish yo'l ko'rsatmaydi, qo'rqitadi.
   */
  const steps = [
    { done: !!years?.length, key: 'Years', to: '/academic-years', icon: IconCalendarTime, allowed: canManageTerms },
    { done: !!terms?.length, key: 'Terms', to: '/academic-years', icon: IconCalendarTime, allowed: canManageTerms },
    { done: !!classes?.length, key: 'Classes', to: '/classes', icon: IconSchool, allowed: canAccess(user?.role, 'classes') },
    { done: !!subjects?.length, key: 'Subjects', to: '/subjects', icon: IconBook, allowed: canAccess(user?.role, 'subjects') },
  ];
  const blocking = lookupsReady ? steps.find((s) => !s.done) : undefined;

  return (
    <>
      <PageHeader title={t('journal.pageTitle')} subtitle={t('journal.pageSubtitle')} />

      {blocking && (
        <Alert
          mb="md"
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title={t(`journal.setupNo${blocking.key}`)}
        >
          <Stack gap="sm" align="flex-start">
            <Text fz="sm" lh={1.5}>{t(`journal.setupNo${blocking.key}Desc`)}</Text>
            {/* To'liq yo'l — mijoz qayerda turganini va keyin nima qilishini ko'rsin */}
            <List spacing={4} size="sm" center icon={<ThemeIcon size={16} radius="xl" variant="light" color="gray"><IconArrowRight size={11} /></ThemeIcon>}>
              {steps.map((s) => (
                <List.Item
                  key={s.key}
                  icon={s.done ? (
                    <ThemeIcon size={16} radius="xl" variant="light" color="teal"><IconCircleCheck size={11} /></ThemeIcon>
                  ) : undefined}
                >
                  <Text fz="sm" lh={1.5} c={s.done ? 'dimmed' : undefined} fw={s.key === blocking.key ? 600 : 400}>
                    {t(`journal.step${s.key}`)}
                  </Text>
                </List.Item>
              ))}
            </List>
            {blocking.allowed ? (
              <Button size="compact-sm" variant="light" leftSection={<blocking.icon size={15} />} onClick={() => navigate(blocking.to)}>
                {t(`journal.goTo${blocking.key}`)}
              </Button>
            ) : (
              <Text fz="sm" c="dimmed" lh={1.5}>{t('journal.askAdmin')}</Text>
            )}
          </Stack>
        </Alert>
      )}

      <Card mb="md">
        <Group gap="sm" align="flex-end" wrap="wrap">
          <Select
            label={t('fields.class')}
            placeholder={classes?.length ? t('common.selectPlaceholder') : t('journal.setupNoClasses')}
            data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={classId}
            onChange={setClassId}
            searchable
            disabled={!classes?.length}
            nothingFoundMessage={t('common.noResults')}
            w={190}
          />
          <Select
            label={t('fields.subject')}
            placeholder={subjects?.length ? t('common.selectPlaceholder') : t('journal.setupNoSubjects')}
            data={(subjects ?? []).map((s) => ({ value: s.id, label: s.name }))}
            value={subjectId}
            onChange={setSubjectId}
            searchable
            disabled={!subjects?.length}
            nothingFoundMessage={t('common.noResults')}
            w={200}
          />
          <Select
            label={t('gradesPage.term')}
            placeholder={terms?.length ? t('gradesPage.selectTerm') : t('journal.noTerms')}
            data={(terms ?? []).map((tm) => ({ value: tm.id, label: tm.name }))}
            value={effectiveTermId}
            onChange={setTermId}
            // Chorak — MAJBURIY kesim: `clearable` bermaslikning o'zi yetarli emas,
            // `allowDeselect` busiz tanlangan variantni qayta bosish tozalab yuborardi.
            allowDeselect={false}
            disabled={!terms?.length}
            w={190}
          />
          <Select
            label={t('journal.period')}
            data={[
              { value: '7', label: t('journal.period7') },
              { value: '14', label: t('journal.period14') },
              { value: '30', label: t('journal.period30') },
              { value: 'chorak', label: t('journal.periodTerm') },
              { value: 'custom', label: t('journal.periodCustom') },
            ]}
            value={davr}
            onChange={(v) => setDavr(v ?? '7')}
            allowDeselect={false}
            w={170}
          />
          {davr === 'custom' && (
            <>
              <DateInput
                {...uzDateProps}
                label={t('journal.dateFrom')}
                value={from}
                onChange={setFrom}
                clearable
                w={150}
              />
              <DateInput
                {...uzDateProps}
                label={t('journal.dateTo')}
                value={to}
                onChange={setTo}
                clearable
                w={150}
              />
            </>
          )}
          {davr !== 'chorak' && (
            <Button variant="subtle" size="compact-sm" onClick={() => setDavr('chorak')}>
              {t('journal.showAll')}
            </Button>
          )}
        </Group>
        {davr !== 'chorak' && (
          /* Taklif filtrdan MUSTAQIL ekani ochiq aytiladi — aks holda
             o'qituvchi "nega 2 ta baho ko'rinadi-yu, taklif 4?" deb hayron
             bo'lardi. */
          <Text size="xs" c="dimmed" mt="xs">{t('journal.dateFilterNote')}</Text>
        )}
      </Card>

      {/* `keepMounted={false}` — ko'rinmayotgan tab so'rov yubormasin */}
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="matrix" leftSection={<IconChecklist size={16} />}>{t('journal.tabMatrix')}</Tabs.Tab>
          <Tabs.Tab value="bulk" leftSection={<IconUsersGroup size={16} />}>{t('journal.tabBulk')}</Tabs.Tab>
          <Tabs.Tab value="term" leftSection={<IconCertificate size={16} />}>{t('journal.tabTerm')}</Tabs.Tab>
          <Tabs.Tab value="report" leftSection={<IconReportAnalytics size={16} />}>{t('journal.tabReport')}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="matrix">
          <JournalMatrix
            classId={classId}
            subjectId={subjectId}
            termId={effectiveTermId}
            from={oraliq.from}
            to={oraliq.to}
            onGoToTerms={canManageTerms ? () => navigate('/academic-years') : undefined}
          />
        </Tabs.Panel>
        <Tabs.Panel value="bulk">
          <BulkGrades classId={classId} subjectId={subjectId} />
        </Tabs.Panel>
        <Tabs.Panel value="term">
          <TermSummary classId={classId} subjectId={subjectId} termId={effectiveTermId} />
        </Tabs.Panel>
        <Tabs.Panel value="report">
          <GradeReport classId={classId} subjectId={subjectId} termId={effectiveTermId} />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
